/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import { isEmpty } from 'lodash';

const isStaticBuildEnabled = () => process.env.NEXT_PUBLIC_STATIC_BUILD_ENABLED === 'true';
// eslint-disable-next-line dot-notation
const areEnvVarsAvailableOnConfig = () => typeof window !== 'undefined' && !isEmpty(window?.['__PORTAL_APP_CONFIG']);

const ENV_MAPPING_STATIC = {
  REACT_APP_DODDLE_API_INTERNAL_URL: 'NEXT_PUBLIC_DODDLE_API_INTERNAL_URL',
  REACT_APP_DODDLE_API_URL: 'NEXT_PUBLIC_DODDLE_API_URL',
  REACT_APP_ALLOWED_ADMIN_ROLES: 'NEXT_PUBLIC_ALLOWED_ADMIN_ROLES',
  REACT_APP_ALLOWED_MERCHANT_ROLES: 'NEXT_PUBLIC_ALLOWED_MERCHANT_ROLES',
  REACT_APP_ASSETS_URL: 'NEXT_PUBLIC_ASSETS_URL',
  REACT_APP_CARRIER: 'NEXT_PUBLIC_CARRIER',
  REACT_APP_CONSUMER_URL: 'NEXT_PUBLIC_CONSUMER_URL',
  REACT_APP_AMPLITUDE_API_KEY: 'NEXT_PUBLIC_AMPLITUDE_API_KEY',
  REACT_APP_LOCATE_PAGE_DOMAIN: 'NEXT_PUBLIC_LOCATE_PAGE_DOMAIN',
  REACT_APP_DEFAULT_LANGUAGE_OVERRIDE: 'NEXT_PUBLIC_DEFAULT_LANGUAGE_OVERRIDE',
  REACT_APP_APPLY_CSP: 'NEXT_PUBLIC_APPLY_CSP',
  REACT_APP_WIDGET_ORIGINS: 'NEXT_PUBLIC_WIDGET_ORIGINS',
  REACT_APP_ORGANISATION_ID: 'NEXT_PUBLIC_ORGANISATION_ID',
  REACT_APP_TIERS_API_KEY: 'NEXT_PUBLIC_TIERS_API_KEY',
  REACT_APP_TIER_1_CONSUMER_CHARGE_ACCOUNT: 'NEXT_PUBLIC_TIER_1_CONSUMER_CHARGE_ACCOUNT',
  REACT_APP_TIER_1_MLID: 'NEXT_PUBLIC_TIER_1_MLID',
  REACT_APP_TIER_2_CONSUMER_CHARGE_ACCOUNT: 'NEXT_PUBLIC_TIER_2_CONSUMER_CHARGE_ACCOUNT',
  REACT_APP_TIER_2_MLID: 'NEXT_PUBLIC_TIER_2_MLID',
  REACT_APP_TIER_3_CONSUMER_CHARGE_ACCOUNT: 'NEXT_PUBLIC_TIER_3_CONSUMER_CHARGE_ACCOUNT',
  REACT_APP_TIER_3_MLID: 'NEXT_PUBLIC_TIER_3_MLID',
  REACT_APP_TIER_4_CONSUMER_CHARGE_ACCOUNT: 'NEXT_PUBLIC_TIER_4_CONSUMER_CHARGE_ACCOUNT',
  REACT_APP_TIER_4_MLID: 'NEXT_PUBLIC_TIER_4_MLID',
  REACT_APP_SENTRY_ENV: 'NEXT_PUBLIC_SENTRY_ENV',
  NEXT_PUBLIC_STATIC_BUILD_ENABLED: 'NEXT_PUBLIC_STATIC_BUILD_ENABLED',
  NEXT_PUBLIC_APPLICATION_MODE: 'NEXT_PUBLIC_APPLICATION_MODE',
  NEXT_PUBLIC_CDN_BASE_PATH: 'NEXT_PUBLIC_CDN_BASE_PATH',
  NEXT_PUBLIC_APIM_ROUTES_ENABLED: 'NEXT_PUBLIC_APIM_ROUTES_ENABLED',
  NEXT_PUBLIC_RBAC_ENABLED: 'NEXT_PUBLIC_RBAC_ENABLED',
  NEXT_PUBLIC_DODDLE_APIM_PATH: 'NEXT_PUBLIC_DODDLE_APIM_PATH',
  NEXT_PUBLIC_DODDLE_API_PATH: 'NEXT_PUBLIC_DODDLE_API_PATH',
  NEXT_PUBLIC_CONFIG_FILE_PATH: 'NEXT_PUBLIC_CONFIG_FILE_PATH',
  NEXT_PUBLIC_APP_INSIGHTS_KEY: 'NEXT_PUBLIC_APP_INSIGHTS_KEY',
};

/**
 * Helper method to grab the requested environment variable from the window object
 * where they will now live when using "react-env", so we don't get any nasty "object not found" errors.
 *
 * Check if the environment variable is on the window object first before using process.env
 *
 * @param environmentVariablePath
 */
const getEnvVar = (environmentVariablePath) => {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-underscore-dangle
    const windowEnvVar = window.__PORTAL_APP_ENV?.[environmentVariablePath];
    if (windowEnvVar) {
      return windowEnvVar;
    }
  }

  return process.env[environmentVariablePath];
};

// In static build, PORTAL_APP_ENV and PORTAL_APP_CONFIG could be used.
// PORTAL_APP_CONFIG is evaluated first
const getEnvVarWithConfigStatic = (environmentVariablePath) => {
  const variableNameStatic = ENV_MAPPING_STATIC[environmentVariablePath];

  if (variableNameStatic) {
    if (areEnvVarsAvailableOnConfig()) {
      // eslint-disable-next-line no-underscore-dangle
      if (typeof window.__PORTAL_APP_CONFIG[variableNameStatic] !== 'undefined') {
        // eslint-disable-next-line no-underscore-dangle
        return window.__PORTAL_APP_CONFIG[variableNameStatic];
      }
    }

    return getEnvVar(variableNameStatic);
  }

  return undefined;
};

const resolvedGetEnvVar = isStaticBuildEnabled() ? getEnvVarWithConfigStatic : getEnvVar;

export default resolvedGetEnvVar;
